import styled from 'styled-components';
import * as React from 'react';
import { IconSize } from '@ardoq/icons';
import { ComponentDetails, HighlightedBy, OnHighlight } from '../types';
import { ComponentDecorator } from '@ardoq/decorators';
import { fontFamily, s16 } from '@ardoq/design-tokens';
import { MinWidthWrapper } from '../atoms';
import { ArdoqId } from '@ardoq/api-types';
import { shortestDistanceToBounds } from '../modelUtils';
import { PointAndAngle, Rectangle, truncateComponentLabel } from '@ardoq/graph';
import { returnZero } from '@ardoq/common-helpers';
import { WithPopover } from '@ardoq/popovers';
import { text1 } from '@ardoq/typography';
import { canvasLabelMeasurer } from '@ardoq/graph-labeling';

const ComponentLabel = styled.div`
  ${text1}
  white-space: nowrap;
`;
// text1 typography: fontSize.large (16px) / lineHeight.regular (20px)
const DEPENDENCY_WHEEL_LABEL_FONT = `400 16px/20px ${fontFamily.regular}`;
const measureLabel = canvasLabelMeasurer(DEPENDENCY_WHEEL_LABEL_FONT);

type AbsolutePositionedComponentsProps = {
  components: ComponentDetails[];
  highlightedComponentId: ArdoqId | null;
  highlightedBy: HighlightedBy;
  onHighlight: OnHighlight;
  componentsWithReferencesToHighlightedComponent: ArdoqId[];
  setComponentInContext: (componentId: ArdoqId) => void;
  componentContextMenuAttr?: string;
  viewBounds: Rectangle;
  componentPositions: Map<ComponentDetails, PointAndAngle>;
};

export const AbsolutePositionedComponents = ({
  components,
  highlightedComponentId,
  onHighlight,
  componentsWithReferencesToHighlightedComponent,
  setComponentInContext,
  componentContextMenuAttr,
  viewBounds,
  componentPositions,
}: AbsolutePositionedComponentsProps) => {
  const absolutePositionedComponents = components.map(component => {
    return (
      <ComponentContainer
        isAnyComponentHighlighted={highlightedComponentId !== null}
        isCurrentComponentHighlighted={highlightedComponentId === component.id}
        onHighlight={onHighlight}
        key={component.id}
        component={component}
        isOutOfHighlight={
          !componentsWithReferencesToHighlightedComponent.includes(component.id)
        }
        setComponentInContext={setComponentInContext}
        componentContextMenuAttr={componentContextMenuAttr}
        viewBounds={viewBounds}
        componentPositions={componentPositions}
      />
    );
  });

  return <>{absolutePositionedComponents}</>;
};

type ComponentProps = {
  component: ComponentDetails;
  isAnyComponentHighlighted: boolean;
  isCurrentComponentHighlighted: boolean;
  onHighlight: OnHighlight;
  isOutOfHighlight: boolean;
  setComponentInContext: (componentId: ArdoqId) => void;
  componentContextMenuAttr?: string;
  viewBounds: Rectangle;
  componentPositions: Map<ComponentDetails, PointAndAngle>;
};

const ComponentContainer = ({
  component,
  isAnyComponentHighlighted,
  isCurrentComponentHighlighted,
  onHighlight,
  isOutOfHighlight,
  setComponentInContext,
  componentContextMenuAttr,
  viewBounds,
  componentPositions,
}: ComponentProps) => {
  const handleSingleOrDoubleClick = (
    event: React.MouseEvent<HTMLDivElement, MouseEvent>
  ) => {
    event.stopPropagation();
    if (event.detail === 2) {
      setComponentInContext(component.id);
    } else {
      onHighlight(component.id, 'click');
    }
  };
  const { labelInfo } = component;
  const [x, y, angle] = componentPositions.get(component)!;

  const labelStartX = x + Math.cos(angle) * (IconSize.MEDIUM + 16);
  const labelStartY = y + Math.sin(angle) * (IconSize.MEDIUM + 16);

  // to find the maximum width of the label, we find the closest distance from the label's start point, continuing at the known angle, to the edge of the view bounds.
  const labelWidth = shortestDistanceToBounds(
    [labelStartX, labelStartY, angle],
    viewBounds
  );

  const label = truncateComponentLabel({
    ...labelInfo,
    measure: measureLabel,
    width: labelWidth,
  });
  const fullLabel = truncateComponentLabel({
    ...labelInfo,
    measure: returnZero,
    width: Infinity,
  });

  return (
    <ComponentWrapper
      className="component"
      $x={x}
      $y={y}
      $deg={component.rotationInDegrees}
      $shouldFlipLabel={component.shouldFlipLabel}
      $isHighlighted={isCurrentComponentHighlighted}
      $isOutOfHighlight={isOutOfHighlight && isAnyComponentHighlighted}
      onMouseEnter={() => onHighlight(component.id, 'hover')}
      onMouseLeave={() => onHighlight(null, 'hover')}
      onClick={handleSingleOrDoubleClick}
      {...(componentContextMenuAttr
        ? { [componentContextMenuAttr]: component.id }
        : {})}
    >
      {/* use icon and label as separate components for more flexibility with spacing */}
      <MinWidthWrapper $minWidth={IconSize.MEDIUM}>
        <ComponentDecorator
          iconSize={IconSize.MEDIUM}
          imageSrc={component.imageSrc}
          shapeId={component.shapeId}
          iconId={component.iconId}
          color={component.color}
        />
      </MinWidthWrapper>

      {label === fullLabel ? (
        <ComponentLabel>{label}</ComponentLabel>
      ) : (
        <WithPopover content={fullLabel}>
          <ComponentLabel>{label}</ComponentLabel>
        </WithPopover>
      )}
    </ComponentWrapper>
  );
};

type ComponentWrapperProps = {
  $x: number;
  $y: number;
  $shouldFlipLabel: boolean;
  $deg: number;
  $isHighlighted: boolean;
  $isOutOfHighlight: boolean;
};

const ComponentWrapper = styled.div.attrs<ComponentWrapperProps>(
  ({ $x, $y, $shouldFlipLabel, $deg }) => ({
    style: {
      left: `${$x}px`,
      top: `${$y}px`,
      transformOrigin: `${IconSize.MEDIUM / 2}px 50%`,
      transform: `
    translateY(-50%)
    translateX(${(IconSize.MEDIUM / 2) * -1}px)
    rotate(${$deg}deg)
    translateX(${$shouldFlipLabel ? `calc(100% - ${IconSize.MEDIUM}px)` : 0})
    scaleX(${$shouldFlipLabel ? -1 : 1})
    scaleY(${$shouldFlipLabel ? -1 : 1})
      `,
    },
  })
)`
  display: flex;
  gap: ${s16};
  position: absolute;
  cursor: pointer;
  user-select: none;

  flex-direction: ${({ $shouldFlipLabel }: ComponentWrapperProps) =>
    $shouldFlipLabel ? 'row-reverse' : 'row'};

  opacity: ${props => props.$isOutOfHighlight && `0.75`};
`;
